// src/components/frontend/products/ProductInfo.tsx
import { Discount } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Rating,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { formatProductOptionValue } from "../../../utils/productOptionValueUtils";
import { formatPrice, formatTjs } from "../../../utils/priceUtils";
import {
  getExactVariantSpecValue,
  getVariantSpecValue,
  hasExactVariantDimension,
} from "../../../utils/productVariantUtils";
import Price from "../../common/Price";

const useStableList = (values: string[]) => {
  const ref = React.useRef<string[]>([]);
  return React.useMemo(() => {
    if (ref.current.length === 0) {
      ref.current = [...values]; // first render = source of truth
      return ref.current;
    }
    // keep previous order, drop removed items, append new ones at the end
    const existing = ref.current.filter((v) => values.includes(v));
    const additions = values.filter((v) => !ref.current.includes(v));
    const merged = [...existing, ...additions];
    ref.current = merged;
    return merged;
  }, [values.join("||")]);
};

const norm = (s: string) => s?.toString().trim().toLowerCase();

// human-friendly size sort
const useNaturalSorter = () => {
  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
    [],
  );
  const rank = (s: string) => {
    const t = s.toLowerCase();
    const map: Record<string, number> = {
      xxs: 0,
      xs: 1,
      "extra small": 1,
      s: 2,
      small: 2,
      m: 3,
      medium: 3,
      l: 4,
      large: 4,
      xl: 5,
      xxl: 6,
      xxxl: 7,
    };
    const key = Object.keys(map).find((k) => t.startsWith(k));
    return key ? map[key] : Number.POSITIVE_INFINITY;
  };
  return (a: string, b: string) => {
    const ra = rank(a),
      rb = rank(b);
    if (ra !== rb) return ra - rb;
    return collator.compare(a, b);
  };
};

interface ProductInfoProps {
  product: {
    title: string;
    brand: string;
    price: number | null;
    originalPrice: number | null;
    stars: number;
    reviewCount: number;
    numSales: number;
    categories: string[];
    availability: string;
    seller: string;
    shippingWeight: string;
    warranty: string;
    allVariants: any[];
    productDetails: Array<{ name: string; value: unknown }>;
    features: string[];
    asin?: string;
  };
  selectedSize: string;
  selectedStyle: string;
  selectedColor: string;
  selectedProvider: string;
  selectedGrade: string;
  selectedCapacity: string;
  selectedSet: string;
  selectedConfiguration?: string;
  selectedFlavorName?: string;
  showPriceHistory: boolean;
  isPhoneLocked?: boolean;
  isAppleCareRestricted?: boolean;
  onSizeChange: (size: string) => void;
  onStyleChange: (style: string) => void;
  onColorChange: (color: string) => void;
  onProviderChange: (provider: string) => void;
  onGradeChange: (grade: string) => void;
  onCapacityChange: (capacity: string) => void;
  onSetChange: (set: string) => void;
  onConfigurationChange?: (configuration: string) => void;
  onFlavorNameChange?: (flavorName: string) => void;
  onPriceHistoryToggle: (show: boolean) => void;
  onVariantSelect?: (productId: string) => void;
  showLongDetails?: boolean;
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  selectedSize,
  selectedStyle,
  selectedColor,
  selectedProvider,
  selectedGrade,
  selectedCapacity,
  selectedSet,
  selectedConfiguration = "",
  selectedFlavorName = "",
  showPriceHistory,
  isPhoneLocked = false,
  isAppleCareRestricted = false,
  onSizeChange,
  onStyleChange,
  onColorChange,
  onProviderChange,
  onGradeChange,
  onCapacityChange,
  onSetChange,
  onConfigurationChange, // may be undefined
  onFlavorNameChange, // may be undefined
  onPriceHistoryToggle, // not used in this file, kept for parity
  onVariantSelect,
  showLongDetails = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: exchangeRate } = useExchangeRate();
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  const [expandedSpecs, setExpandedSpecs] = useState<Record<number, boolean>>(
    {},
  );
  const toggleSpecExpansion = (index: number) => {
    setExpandedSpecs((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Check if AppleCare+ is selected (Configuration or Set) - but NOT "Without AppleCare+"
  const isAppleCareSelected = useMemo(() => {
    const configAppleCare =
      selectedConfiguration?.toLowerCase().includes("applecare") &&
      !selectedConfiguration?.toLowerCase().includes("without applecare");
    const setAppleCare =
      selectedSet?.toLowerCase().includes("applecare") &&
      !selectedSet?.toLowerCase().includes("without applecare");
    return configAppleCare || setAppleCare;
  }, [selectedConfiguration, selectedSet]);

  // Use combined AppleCare+ restriction (from parent) or local detection
  const shouldShowAppleCareWarning =
    isAppleCareRestricted || isAppleCareSelected;
  const formatOptionDisplayValue = (_dimension: string, value: string) => {
    if (!value) {
      return value;
    }

    return formatProductOptionValue(value);
  };
  const fragranceDimension = useMemo(
    () =>
      hasExactVariantDimension(product.allVariants, "Scent")
        ? "Scent"
        : hasExactVariantDimension(product.allVariants, "Flavor")
          ? "Flavor"
          : "Flavor Name",
    [product.allVariants],
  );
  const fragranceLabel = useMemo(
    () =>
      fragranceDimension === "Scent"
        ? t("products.scent")
        : t("products.flavorName"),
    [fragranceDimension, t],
  );
  const fragrancePlaceholder = useMemo(
    () =>
      fragranceDimension === "Scent"
        ? t("products.selectScent")
        : t("products.selectFlavorName"),
    [fragranceDimension, t],
  );
  const getVariantValueForDimension = (variant: any, dimension: string) =>
    dimension === fragranceDimension
      ? getExactVariantSpecValue(variant, dimension)
      : getVariantSpecValue(variant, dimension);

  // Helpers to compute availability across dimensions
  // Selection-aware availability helpers
  const buildCurrentSpecs = (): Record<string, string> => {
    const s: Record<string, string> = {};
    if (selectedSize) s["Size"] = selectedSize;
    if (selectedStyle) s["Style"] = selectedStyle;
    if (selectedColor) s["Color"] = selectedColor;
    if (selectedProvider) s["Service Provider"] = selectedProvider;
    if (selectedGrade) s["Product Grade"] = selectedGrade;
    if (selectedCapacity) s["Capacity"] = selectedCapacity;
    if (selectedSet) s["Set"] = selectedSet;
    if (selectedConfiguration) s["Configuration"] = selectedConfiguration;
    if (selectedFlavorName) s[fragranceDimension] = selectedFlavorName;
    return s;
  };

  const filterVariantsBySpecs = (
    specs: Record<string, string>,
    omitDimension?: string,
  ) => {
    return product.allVariants.filter((variant: any) => {
      return Object.entries(specs).every(([uiDim, val]) => {
        if (omitDimension && uiDim === omitDimension) return true;
        const foundValue = getVariantValueForDimension(variant, uiDim);
        if (!foundValue) return true; // dimension not specified → treat as compatible
        if (uiDim === "Color") {
          return foundValue.toLowerCase().trim() === val.toLowerCase().trim();
        }
        return foundValue === val;
      });
    });
  };

  const getAvailableForDimension = (
    dimension: string,
    overrides?: Record<string, string>,
  ): string[] => {
    const specs = { ...buildCurrentSpecs(), ...(overrides || {}) };
    const filtered = filterVariantsBySpecs(specs, dimension);
    const values = new Set<string>();
    for (const v of filtered) {
      const value = getVariantValueForDimension(v, dimension);
      if (value) values.add(value);
    }
    return Array.from(values);
  };

  // All values for a dimension across all variants (unfiltered)
  const getAllValuesForDimension = (dimension: string): string[] => {
    const values = new Set<string>();
    for (const v of product.allVariants) {
      const value = getVariantValueForDimension(v, dimension);
      if (value) values.add(value);
    }
    return Array.from(values);
  };

  // Extract unique lists (selection-aware)
  const uniqueCapacities = Array.from(
    new Set(getAvailableForDimension("Capacity")),
  ) as string[];

  // CPU
  const uniqueCpus = Array.from(
    new Set(getAvailableForDimension("CPU")),
  ) as string[];

  const uniqueColors = Array.from(
    new Set(getAvailableForDimension("Color")),
  ) as string[];

  const uniqueSets = Array.from(
    new Set(getAvailableForDimension("Set")),
  ) as string[];

  // Sizes (sorted)
  const naturalSort = useNaturalSorter();
  const allSizes = useMemo(() => {
    const sizes = getAllValuesForDimension("Size");
    return Array.from(new Set(sizes)).sort(naturalSort);
  }, [product.allVariants, naturalSort]);

  const uniqueSizes = useMemo(() => {
    const sizes = getAvailableForDimension("Size");
    return Array.from(new Set(sizes)).sort(naturalSort);
  }, [
    selectedSize,
    selectedStyle,
    selectedColor,
    selectedProvider,
    selectedGrade,
    selectedCapacity,
    selectedSet,
    selectedConfiguration,
    selectedFlavorName,
    product.allVariants,
    naturalSort,
  ]);
  const availableSizeSet = useMemo(() => new Set(uniqueSizes), [uniqueSizes]);

  const uniqueStyles = Array.from(
    new Set(getAvailableForDimension("Style")),
  ) as string[];

  // Removed nested Size-under-Style mapping; grouping is driven by availability across all dimensions

  const providersRaw = Array.from(
    new Set(getAvailableForDimension("Service Provider")),
  ) as string[];
  const uniqueProviders = useStableList(providersRaw);

  const gradesRaw = Array.from(
    new Set(getAvailableForDimension("Product Grade")),
  ) as string[];
  const uniqueGrades = useStableList(gradesRaw);

  // Configuration list (stable order)
  const configsRaw = Array.from(
    new Set(getAvailableForDimension("Configuration")),
  ) as string[];
  const uniqueConfigurations = useStableList(configsRaw);

  // Scent / Flavor Name list (stable order)
  const fragranceValuesRaw = Array.from(
    new Set(getAvailableForDimension(fragranceDimension)),
  ) as string[];
  const uniqueFragranceValues = useStableList(fragranceValuesRaw);

  // Pattern Name list
  const patternsRaw = Array.from(
    new Set(getAvailableForDimension("Pattern Name")),
  ) as string[];
  const uniquePatterns = useStableList(patternsRaw);

  // Init selecteds from current variant
  useEffect(() => {
    const current = product.allVariants.find(
      (v: any) => v.product_id === product.asin || v.asin === product.asin,
    );

    const curSize = current ? getVariantSpecValue(current, "Size") : undefined;
    const curStyle = current
      ? getVariantSpecValue(current, "Style")
      : undefined;
    const curProvider = current
      ? getVariantSpecValue(current, "Service Provider")
      : undefined;
    const curGrade = current
      ? getVariantSpecValue(current, "Product Grade")
      : undefined;
    const curConfig = current
      ? getVariantSpecValue(current, "Configuration")
      : undefined;
    const curFlavorName = current
      ? getVariantValueForDimension(current, fragranceDimension)
      : undefined;

    if (curSize && norm(selectedSize) !== norm(curSize)) onSizeChange(curSize);

    if (curStyle) {
      const stylesForCurSize = curSize
        ? Array.from(
            new Set(
              product.allVariants
                .filter((v: any) =>
                  v.variant_specifics?.some(
                    (s: any) => s.dimension === "Size" && s.value === curSize,
                  ),
                )
                .map((v: any) => getVariantSpecValue(v, "Style"))
                .filter(Boolean) as string[],
            ),
          )
        : [];
      const selectedStillValid =
        selectedStyle &&
        stylesForCurSize.some((s) => norm(s) === norm(selectedStyle));
      if (!selectedStillValid || norm(selectedStyle) !== norm(curStyle))
        onStyleChange(curStyle);
    }

    if (curProvider && norm(selectedProvider) !== norm(curProvider))
      onProviderChange(curProvider);

    if (curGrade && norm(selectedGrade) !== norm(curGrade))
      onGradeChange(curGrade);

    if (curConfig && norm(selectedConfiguration) !== norm(curConfig))
      onConfigurationChange?.(curConfig);

    if (curFlavorName && norm(selectedFlavorName) !== norm(curFlavorName))
      onFlavorNameChange?.(curFlavorName);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.asin, product.allVariants]);

  // Find variant by UI specs
  const findVariantBySpecs = (specs: Record<string, string>) => {
    const foundVariant = product.allVariants.find((variant) => {
      return Object.entries(specs).every(([dimension, value]) => {
        const variantValue = getVariantValueForDimension(variant, dimension);
        if (!variantValue) {
          return true; // dimension not specified → treat as compatible
        }
        if (dimension === "Color") {
          return (
            variantValue.toLowerCase().trim() === value.toLowerCase().trim()
          );
        }
        return variantValue === value;
      });
    });

    return foundVariant;
  };

  // Selection handler
  const handleVariantSelect = (dimension: string, value: string) => {
    const currentSpecs = buildCurrentSpecs();

    currentSpecs[dimension] = value;

    const n = (s: string) => s?.trim().toLowerCase();

    if (dimension === "Size") {
      // First update the size
      onSizeChange?.(value);

      // Check if current style is still valid for the new size
      const stylesForSize = Array.from(
        new Set(
          product.allVariants
            .filter((v: any) =>
              v.variant_specifics?.some(
                (s: any) => s.dimension === "Size" && s.value === value,
              ),
            )
            .map((v: any) => getVariantSpecValue(v, "Style"))
            .filter(Boolean),
        ),
      ) as string[];

      const styleStillValid =
        selectedStyle && stylesForSize.some((s) => n(s) === n(selectedStyle));

      // Only change style if it's not valid for the new size
      if (!styleStillValid && stylesForSize.length > 0) {
        const newStyle = stylesForSize[0];
        currentSpecs["Style"] = newStyle;
        onStyleChange?.(newStyle);
      }
    }

    if (dimension === "Style") {
      // First update the style
      onStyleChange?.(value);

      // Check if current size is still valid for the new style
      const sizesForStyle = Array.from(
        new Set(
          product.allVariants
            .filter((v: any) =>
              v.variant_specifics?.some(
                (s: any) => s.dimension === "Style" && s.value === value,
              ),
            )
            .map((v: any) => getVariantSpecValue(v, "Size"))
            .filter(Boolean),
        ),
      ) as string[];

      const sizeStillValid =
        selectedSize && sizesForStyle.some((s) => n(s) === n(selectedSize));

      // Only change size if it's not valid for the new style
      if (!sizeStillValid && sizesForStyle.length > 0) {
        const newSize = sizesForStyle[0];
        currentSpecs["Size"] = newSize;
        onSizeChange?.(newSize);
      }
    }

    if (dimension === "Color") onColorChange?.(value);
    if (dimension === "Service Provider") onProviderChange?.(value);
    if (dimension === "Product Grade") onGradeChange?.(value);
    if (dimension === "Capacity") onCapacityChange?.(value);
    if (dimension === "Set") onSetChange?.(value);
    if (dimension === "Configuration") onConfigurationChange?.(value);
    if (dimension === fragranceDimension) onFlavorNameChange?.(value);
    if (dimension === "CPU") onCapacityChange?.(selectedCapacity); // no-op hook here, CPU handled via variant navigation
    if (dimension === "Pattern Name") onColorChange?.(selectedColor); // no-op, state carried in variant

    const targetVariant = findVariantBySpecs(currentSpecs);

    if (targetVariant && onVariantSelect) {
      onVariantSelect(targetVariant.product_id);
      return;
    }

    const fallbackVariant = product.allVariants.find((variant) => {
      const variantValue = getVariantValueForDimension(variant, dimension);
      if (!variantValue) {
        return false;
      }

      if (dimension === "Color") {
        return variantValue.toLowerCase().trim() === value.toLowerCase().trim();
      }

      return variantValue === value;
    });

    if (fallbackVariant && onVariantSelect) {
      onVariantSelect(fallbackVariant.product_id);
    }
  };

  const ratingValue = Number.isFinite(product.stars) ? product.stars : 0;
  const reviewCountValue = Number.isFinite(product.reviewCount)
    ? product.reviewCount
    : 0;
  const hasRatingValue = ratingValue > 0;
  const hasReviewCount = reviewCountValue > 0;
  const showRatings = hasRatingValue;

  return (
    <Stack spacing={isMobile ? 2 : 3}>
      {/* Product Title */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          lineHeight: { xs: 1.35, sm: 1.3 },
          color: amazonColors.text.primary,
          mb: 1,
          wordBreak: "break-word",
        }}
      >
        {product.title}
      </Typography>

      {/* Brand */}
      {product.brand && (
        <Typography
          variant="body2"
          sx={{ color: amazonColors.text.secondary, mb: 0.5 }}
        >
          Brand:{" "}
          <Box
            component="span"
            sx={{
              color: "#007185",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": { color: "#C7511F", textDecoration: "underline" },
            }}
          >
            {product.brand}
          </Box>
        </Typography>
      )}
      {/* Ratings */}
      {showRatings && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Rating
            value={ratingValue}
            precision={0.1}
            readOnly
            size="small"
            sx={{
              "& .MuiRating-iconFilled": { color: "#ffa41c" },
              "& .MuiRating-iconHover": { color: "#ffa41c" },
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: amazonColors.text.primary, fontWeight: 500 }}
          >
            {ratingValue} out of 5 stars
          </Typography>
          {hasReviewCount && (
            <Typography
              variant="body2"
              sx={{ color: amazonColors.text.secondary, ml: 1 }}
            >
              {reviewCountValue.toLocaleString()} ratings
            </Typography>
          )}
        </Box>
      )}

      {showRatings && <Divider sx={{ my: 1 }} />}

      {/* Locked Phone Warning */}
      {isPhoneLocked && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            "& .MuiAlert-icon": { color: "#856404" },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#856404" }}
          >
            {t("products.phoneLockedWarning")}
          </Typography>
        </Alert>
      )}

      {/* AppleCare+ Warning */}
      {shouldShowAppleCareWarning && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            "& .MuiAlert-icon": { color: "#856404" },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#856404" }}
          >
            AppleCare+ not available in Tajikistan. Select ‘Without AppleCare+’
          </Typography>
        </Alert>
      )}

      {/* Price Section */}
      <Box sx={{ mb: 3 }}>
        {product.price !== null ? (
          (() => {
            const tjsPrice = convertUsdToTjs(
              Number(product.price),
              exchangeRate ?? null,
            );
            const formatted = formatPrice(product.price);
            const match = formatted.match(/^(\D*)(-?\d+)\.(\d{2})$/);
            const symbol = match ? match[1] || "$" : "$";
            const dollars = match
              ? match[2]
              : Number(product.price).toFixed(2).split(".")[0];
            const cents = match
              ? match[3]
              : Number(product.price).toFixed(2).split(".")[1];

            // TJS-first: show TJS as primary, USD as secondary
            const primaryPrice =
              tjsPrice !== null ? (
                <Box
                  translate="no"
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.5,
                    mb: 0.25,
                    color: amazonColors.text.primary,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, fontSize: "1.8rem", lineHeight: 1 }}
                  >
                    {formatTjs(tjsPrice)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontWeight: 600, fontSize: "1rem" }}
                  >
                    c.
                  </Typography>
                  {product.originalPrice !== null &&
                    product.price !== null &&
                    product.originalPrice > product.price && (
                      <Typography
                        translate="no"
                        variant="h6"
                        component="span"
                        sx={{
                          textDecoration: "line-through",
                          color: amazonColors.text.secondary,
                          fontWeight: 400,
                          ml: 2,
                        }}
                      >
                        <Price
                          value={product.originalPrice}
                          tjsAmount={convertUsdToTjs(
                            Number(product.originalPrice),
                            exchangeRate ?? null,
                          )}
                        />
                      </Typography>
                    )}
                </Box>
              ) : (
                <Box
                  translate="no"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    mb: 1,
                    color: amazonColors.text.primary,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontWeight: 500, fontSize: "1rem" }}
                  >
                    {symbol}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontWeight: 600, fontSize: "1.8rem", lineHeight: 1 }}
                  >
                    {dollars}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontWeight: 500, fontSize: "1rem" }}
                  >
                    {cents}
                  </Typography>
                  {product.originalPrice !== null &&
                    product.price !== null &&
                    product.originalPrice > product.price && (
                      <Typography
                        translate="no"
                        variant="h6"
                        component="span"
                        sx={{
                          textDecoration: "line-through",
                          color: amazonColors.text.secondary,
                          fontWeight: 400,
                          ml: 2,
                        }}
                      >
                        <Price value={product.originalPrice} />
                      </Typography>
                    )}
                </Box>
              );

            return (
              <>
                {primaryPrice}
                {/* USD shown as secondary when TJS is available */}
                {tjsPrice !== null && (
                  <Typography
                    variant="body2"
                    translate="no"
                    sx={{
                      color: "#888",
                      fontWeight: 400,
                      fontSize: "0.85rem",
                      mb: 1,
                    }}
                  >
                    ({formatPrice(product.price)})
                  </Typography>
                )}
              </>
            );
          })()
        ) : (
          <Typography
            variant="body2"
            sx={{ color: amazonColors.text.secondary, mb: 1 }}
            className="notranslate"
            translate="no"
          >
            {t("products.priceNotAvailable")}
          </Typography>
        )}

        {product.originalPrice !== null &&
          product.price !== null &&
          product.originalPrice > product.price && (
            <Chip
              label={
                <>
                  Save{" "}
                  <Price
                    value={product.originalPrice - product.price}
                    tjsAmount={convertUsdToTjs(
                      Number(product.originalPrice) - Number(product.price),
                      exchangeRate ?? null,
                    )}
                  />
                </>
              }
              size="small"
              icon={<Discount color="inherit" />}
              sx={{
                backgroundColor: amazonColors.green,
                color: "white",
                fontWeight: 600,
                fontSize: "0.75rem",
                mb: 2,
              }}
            />
          )}
      </Box>

      {/* Color (text buttons) - show all, disable invalid */}
      {getAllValuesForDimension("Color").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.color")}{" "}
            {formatOptionDisplayValue("Color", selectedColor)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Color");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedColor || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueColors.includes(val);
                    if (isAvailable) handleVariantSelect("Color", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectColor")}
                  </MenuItem>
                  {all.map((color) => {
                    const isAvailable = uniqueColors.includes(color);
                    return (
                      <MenuItem
                        key={color}
                        value={color}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue("Color", color)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((color) => {
                  const isAvailable = uniqueColors.includes(color);
                  return (
                    <Button
                      key={color}
                      variant={
                        selectedColor === color ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable && handleVariantSelect("Color", color)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedColor === color ? "2px solid" : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedColor === color
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedColor === color
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedColor === color
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedColor === color
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedColor === color
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Color", color)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Capacity - show all, disable invalid */}
      {getAllValuesForDimension("Capacity").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.capacity")}{" "}
            {formatOptionDisplayValue("Capacity", selectedCapacity)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Capacity");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedCapacity || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueCapacities.includes(val);
                    if (isAvailable) handleVariantSelect("Capacity", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectCapacity")}
                  </MenuItem>
                  {all.map((capacity) => {
                    const isAvailable = uniqueCapacities.includes(capacity);
                    return (
                      <MenuItem
                        key={capacity}
                        value={capacity}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue("Capacity", capacity)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((capacity) => {
                  const isAvailable = uniqueCapacities.includes(capacity);
                  return (
                    <Button
                      key={capacity}
                      variant={
                        selectedCapacity === capacity ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable && handleVariantSelect("Capacity", capacity)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedCapacity === capacity
                            ? "2px solid"
                            : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedCapacity === capacity
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedCapacity === capacity
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedCapacity === capacity
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedCapacity === capacity
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedCapacity === capacity
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Capacity", capacity)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* CPU - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("CPU").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.cpu")}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("CPU");
            if (all.length > 5) {
              return (
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueCpus.includes(val);
                    if (isAvailable) handleVariantSelect("CPU", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectCpu")}
                  </MenuItem>
                  {all.map((cpu) => {
                    const isAvailable = uniqueCpus.includes(cpu);
                    return (
                      <MenuItem key={cpu} value={cpu} disabled={!isAvailable}>
                        {formatOptionDisplayValue("CPU", cpu)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((cpu) => {
                  const isAvailable = uniqueCpus.includes(cpu);
                  return (
                    <Button
                      key={cpu}
                      variant={"outlined"}
                      onClick={() =>
                        isAvailable && handleVariantSelect("CPU", cpu)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("CPU", cpu)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Set - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("Set").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.set")} {formatOptionDisplayValue("Set", selectedSet)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Set");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedSet || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueSets.includes(val);
                    if (isAvailable) handleVariantSelect("Set", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectSet")}
                  </MenuItem>
                  {all.map((set) => {
                    const isAvailable = uniqueSets.includes(set);
                    return (
                      <MenuItem key={set} value={set} disabled={!isAvailable}>
                        {formatOptionDisplayValue("Set", set)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((set) => {
                  const isAvailable = uniqueSets.includes(set);
                  return (
                    <Button
                      key={set}
                      variant={selectedSet === set ? "contained" : "outlined"}
                      onClick={() =>
                        isAvailable && handleVariantSelect("Set", set)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border: selectedSet === set ? "2px solid" : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedSet === set
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedSet === set
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedSet === set
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedSet === set
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedSet === set
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Set", set)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Size - dropdown if > 5, else buttons */}
      {allSizes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: amazonColors.text.primary,
                fontSize: "1rem",
                mb: 0,
              }}
              className="notranslate"
              translate="no"
            >
              {t("products.size")}{" "}
              {formatOptionDisplayValue("Size", selectedSize)}
            </Typography>
          </Box>
          {(() => {
            if (allSizes.length > 5) {
              return (
                <Select
                  value={selectedSize || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = availableSizeSet.has(val);
                    if (isAvailable) handleVariantSelect("Size", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectSize")}
                  </MenuItem>
                  {allSizes.map((size) => {
                    const isAvailable = availableSizeSet.has(size);
                    return (
                      <MenuItem key={size} value={size} disabled={!isAvailable}>
                        {formatOptionDisplayValue("Size", size)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {allSizes.map((size) => {
                  const isAvailable = availableSizeSet.has(size);
                  return (
                    <Button
                      key={size}
                      variant={selectedSize === size ? "contained" : "outlined"}
                      onClick={() =>
                        isAvailable && handleVariantSelect("Size", size)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedSize === size ? "2px solid" : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedSize === size
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedSize === size
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedSize === size
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedSize === size
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedSize === size
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Size", size)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Style - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("Style").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.style")}{" "}
            {formatOptionDisplayValue("Style", selectedStyle)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Style");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedStyle || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueStyles.includes(val);
                    if (isAvailable) handleVariantSelect("Style", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectStyle")}
                  </MenuItem>
                  {all.map((style) => {
                    const isAvailable = uniqueStyles.includes(style);
                    return (
                      <MenuItem
                        key={style}
                        value={style}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue("Style", style)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((style) => {
                  const isAvailable = uniqueStyles.includes(style);
                  return (
                    <Button
                      key={style}
                      variant={
                        selectedStyle === style ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable && handleVariantSelect("Style", style)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedStyle === style ? "2px solid" : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedStyle === style
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedStyle === style
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedStyle === style
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedStyle === style
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedStyle === style
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Style", style)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}

          {/* Nested Size under Style removed - using flat Size group */}
        </Box>
      )}

      {/* Service Provider - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("Service Provider").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.serviceProvider")}{" "}
            {formatOptionDisplayValue("Service Provider", selectedProvider)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Service Provider");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedProvider || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueProviders.includes(val);
                    if (isAvailable)
                      handleVariantSelect("Service Provider", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectServiceProvider")}
                  </MenuItem>
                  {all.map((provider) => {
                    const isAvailable = uniqueProviders.includes(provider);
                    return (
                      <MenuItem
                        key={provider}
                        value={provider}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue("Service Provider", provider)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((provider) => {
                  const isAvailable = uniqueProviders.includes(provider);
                  return (
                    <Button
                      key={provider}
                      variant={
                        selectedProvider === provider ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable &&
                        handleVariantSelect("Service Provider", provider)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedProvider === provider
                            ? "2px solid"
                            : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedProvider === provider
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedProvider === provider
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedProvider === provider
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedProvider === provider
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedProvider === provider
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Service Provider", provider)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Product Grade - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("Product Grade").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.productGrade")}{" "}
            {formatOptionDisplayValue("Product Grade", selectedGrade)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Product Grade");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedGrade || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueGrades.includes(val);
                    if (isAvailable) handleVariantSelect("Product Grade", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectProductGrade")}
                  </MenuItem>
                  {all.map((grade) => {
                    const isAvailable = uniqueGrades.includes(grade);
                    return (
                      <MenuItem
                        key={grade}
                        value={grade}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue("Product Grade", grade)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((grade) => {
                  const isAvailable = uniqueGrades.includes(grade);
                  return (
                    <Button
                      key={grade}
                      variant={
                        selectedGrade === grade ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable &&
                        handleVariantSelect("Product Grade", grade)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedGrade === grade ? "2px solid" : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedGrade === grade
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedGrade === grade
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedGrade === grade
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedGrade === grade
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedGrade === grade
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Product Grade", grade)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Configuration - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("Configuration").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.configuration")}{" "}
            {formatOptionDisplayValue("Configuration", selectedConfiguration)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Configuration");
            if (all.length > 5) {
              return (
                <Select
                  value={selectedConfiguration || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueConfigurations.includes(val);
                    if (isAvailable) handleVariantSelect("Configuration", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectConfiguration")}
                  </MenuItem>
                  {all.map((cfg) => {
                    const isAvailable = uniqueConfigurations.includes(cfg);
                    return (
                      <MenuItem key={cfg} value={cfg} disabled={!isAvailable}>
                        {formatOptionDisplayValue("Configuration", cfg)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((cfg) => {
                  const isAvailable = uniqueConfigurations.includes(cfg);
                  return (
                    <Button
                      key={cfg}
                      variant={
                        selectedConfiguration === cfg ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable && handleVariantSelect("Configuration", cfg)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedConfiguration === cfg
                            ? "2px solid"
                            : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedConfiguration === cfg
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedConfiguration === cfg
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedConfiguration === cfg
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedConfiguration === cfg
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedConfiguration === cfg
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Configuration", cfg)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Scent / Flavor Name - dropdown if > 5, else buttons */}
      {getAllValuesForDimension(fragranceDimension).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {fragranceLabel}{" "}
            {formatOptionDisplayValue(fragranceDimension, selectedFlavorName)}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension(fragranceDimension);
            if (all.length > 5) {
              return (
                <Select
                  value={selectedFlavorName || ""}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniqueFragranceValues.includes(val);
                    if (isAvailable)
                      handleVariantSelect(fragranceDimension, val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {fragrancePlaceholder}
                  </MenuItem>
                  {all.map((flavor) => {
                    const isAvailable = uniqueFragranceValues.includes(flavor);
                    return (
                      <MenuItem
                        key={flavor}
                        value={flavor}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue(fragranceDimension, flavor)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((flavor) => {
                  const isAvailable = uniqueFragranceValues.includes(flavor);
                  return (
                    <Button
                      key={flavor}
                      variant={
                        selectedFlavorName === flavor ? "contained" : "outlined"
                      }
                      onClick={() =>
                        isAvailable &&
                        handleVariantSelect(fragranceDimension, flavor)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border:
                          selectedFlavorName === flavor
                            ? "2px solid"
                            : "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : selectedFlavorName === flavor
                            ? amazonColors.blue
                            : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : selectedFlavorName === flavor
                            ? amazonColors.blue
                            : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : selectedFlavorName === flavor
                            ? "white"
                            : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : selectedFlavorName === flavor
                              ? amazonColors.blueHover
                              : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : selectedFlavorName === flavor
                              ? amazonColors.blueHover
                              : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue(fragranceDimension, flavor)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Pattern Name - dropdown if > 5, else buttons */}
      {getAllValuesForDimension("Pattern Name").length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: amazonColors.text.primary,
              fontSize: "1rem",
            }}
            className="notranslate"
            translate="no"
          >
            {t("products.patternName")}
          </Typography>
          {(() => {
            const all = getAllValuesForDimension("Pattern Name");
            if (all.length > 5) {
              return (
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (!val) return;
                    const isAvailable = uniquePatterns.includes(val);
                    if (isAvailable) handleVariantSelect("Pattern Name", val);
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" disabled>
                    {t("products.selectPatternName")}
                  </MenuItem>
                  {all.map((pattern) => {
                    const isAvailable = uniquePatterns.includes(pattern);
                    return (
                      <MenuItem
                        key={pattern}
                        value={pattern}
                        disabled={!isAvailable}
                      >
                        {formatOptionDisplayValue("Pattern Name", pattern)}
                      </MenuItem>
                    );
                  })}
                </Select>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {all.map((pattern) => {
                  const isAvailable = uniquePatterns.includes(pattern);
                  return (
                    <Button
                      key={pattern}
                      variant={"outlined"}
                      onClick={() =>
                        isAvailable &&
                        handleVariantSelect("Pattern Name", pattern)
                      }
                      disabled={!isAvailable}
                      sx={{
                        minWidth: 64,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: !isAvailable
                          ? amazonColors.gray[200]
                          : amazonColors.gray[300],
                        backgroundColor: !isAvailable
                          ? amazonColors.gray[100]
                          : "transparent",
                        color: !isAvailable
                          ? amazonColors.gray[400]
                          : amazonColors.text.primary,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: !isAvailable
                            ? amazonColors.gray[100]
                            : amazonColors.gray[100],
                          borderColor: !isAvailable
                            ? amazonColors.gray[200]
                            : amazonColors.blue,
                        },
                      }}
                    >
                      {formatOptionDisplayValue("Pattern Name", pattern)}
                    </Button>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      )}

      {showLongDetails && (
        <>
          {/* Product Specifications */}
          {product.productDetails && product.productDetails.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: amazonColors.text.primary,
                  fontSize: "1rem",
                }}
              >
                Product Specifications
              </Typography>
              <Grid container spacing={2}>
                {product.productDetails
                  .slice(0, showAllSpecs ? product.productDetails.length : 10)
                  .map((detail: any, index: number) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: amazonColors.text.primary,
                        }}
                      >
                        {detail.name}:
                      </Typography>
                      {(() => {
                        const rawValue = detail.value;
                        const fallbackValue =
                          rawValue == null ? "" : String(rawValue);
                        const displayValue =
                          rawValue == null ? "" : String(rawValue);
                        const isExpandable =
                          typeof displayValue === "string" &&
                          displayValue.length > 160;
                        const isExpanded = !!expandedSpecs[index];

                        if (typeof displayValue === "string") {
                          if (isExpandable) {
                            return (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={
                                    isExpanded
                                      ? {}
                                      : {
                                          display: "-webkit-box",
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                        }
                                  }
                                >
                                  {displayValue}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  onClick={() => toggleSpecExpansion(index)}
                                  sx={{
                                    color: amazonColors.blue,
                                    cursor: "pointer",
                                    mt: 0.5,
                                    fontSize: "0.875rem",
                                    "&:hover": {
                                      color: amazonColors.blueHover,
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {isExpanded ? "See less" : "See more"}
                                </Typography>
                              </Box>
                            );
                          }

                          return (
                            <Typography variant="body2" color="text.secondary">
                              {displayValue}
                            </Typography>
                          );
                        }

                        return (
                          <Typography variant="body2" color="text.secondary">
                            {fallbackValue}
                          </Typography>
                        );
                      })()}
                    </Grid>
                  ))}
              </Grid>
              {product.productDetails.length > 10 && (
                <Typography
                  variant="body2"
                  onClick={() => setShowAllSpecs(!showAllSpecs)}
                  sx={{
                    color: amazonColors.blue,
                    cursor: "pointer",
                    mt: 1,
                    fontSize: "0.875rem",
                    "&:hover": {
                      color: amazonColors.blueHover,
                      textDecoration: "underline",
                    },
                  }}
                >
                  {showAllSpecs ? "▲ See less" : "▼ See more"}
                </Typography>
              )}
            </Box>
          )}

          {/* About this item */}
          {product.features && product.features.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: amazonColors.text.primary,
                  fontSize: "1rem",
                }}
              >
                About this item
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {(showMoreFeatures
                  ? product.features
                  : product.features.slice(0, 3)
                ).map((feature: string, index: number) => (
                  <li key={index}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {feature}
                    </Typography>
                  </li>
                ))}
              </Box>
              {product.features.length > 3 && (
                <Button
                  onClick={() => setShowMoreFeatures((p) => !p)}
                  sx={{
                    color: amazonColors.blue,
                    cursor: "pointer",
                    mt: 1,
                    fontSize: "0.875rem",
                    "&:hover": {
                      color: amazonColors.blueHover,
                      textDecoration: "underline",
                    },
                  }}
                >
                  {showMoreFeatures ? "▲ See less" : "▼ See more"}
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default ProductInfo;
